import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, Group, Pagination, Stack, Text } from '@mantine/core';
import { IconBuilding, IconInfoCircle } from '@tabler/icons-react';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';
import { ProtectedComponent } from '@components/ProtectedComponent';
import { FallbackType, UserRole } from '@shared-types/auth.types';
import { PageHeader } from '@components/ui/PageHeader';
import { SystemEnvironmentAndApplicationsTable } from '@features/customers/components/system-assignment/SystemEnvironmentAndApplicationsTable';
import { useQuery } from '@tanstack/react-query';
import { unassignedSystemsQueryOptions } from '@api/customers/customerQueries';
import { SystemCustomerAssignmentModal } from '@features/customers/components/system-assignment/SystemCustomerAssignmentModal';

const searchSchema = z.object({
  page: z.number().catch(1),
  pageSize: z.number().catch(20),
});

export const Route = createFileRoute('/_authed/systems/unassigned/')({
  validateSearch: zodValidator(searchSchema),
  component: UnassignedSystemsPage,
});

function UnassignedSystemsPage() {
  const navigate = Route.useNavigate();
  const {page, pageSize} = Route.useSearch();

  // UI-only state for selections and modal
  const [selectedSystemIds, setSelectedSystemIds] = useState<string[]>([]);
  const [assignmentModalOpened, setAssignmentModalOpened] = useState(false);

  // Queries
  const {
    data: unassignedSystemsData,
    isLoading,
    error
  } = useQuery(unassignedSystemsQueryOptions({page, pageSize}));


  // Handlers
  const handlePageChange = (newPage: number) => {
    navigate({
      search: (prev) => ({...prev, page: newPage})
    });
    setSelectedSystemIds([]);
  };

  const handleModalClose = useCallback(() => {
    setAssignmentModalOpened(false);
    setSelectedSystemIds([]);
  }, []);

  // Computed values
  const totalPages = unassignedSystemsData
    ? Math.ceil(unassignedSystemsData.totalCount / pageSize)
    : 0;

  const selectedSystems = useMemo(() =>
      unassignedSystemsData?.systems
        .filter(system => selectedSystemIds.includes(system.systemId))
        .map(system => ({
          systemId: system.systemId,
          machineName: system.systemEnvironmentAndApplications.machineName
        })) ?? [],
    [unassignedSystemsData, selectedSystemIds]
  );

  return (
    <ProtectedComponent
      requiredRole={UserRole.Support}
      fallbackType={FallbackType.Page}
      fallbackTitle="Unassigned Systems Management"
      fallbackAction="manage unassigned systems"
    >
      <Stack gap="lg" mah="calc(100vh - var(--app-shell-header-height) - var(--mantine-spacing-xl))">
        <PageHeader
          title="Unassigned Systems Management"
          subtitle="Select systems and assign them to customers"
          backButton={false}
        />

        {error && (
          <Alert
            variant="light"
            color="red"
            icon={<IconInfoCircle/>}
            title="Error loading unassigned systems"
          >
            Failed to load unassigned systems. Please try refreshing the page.
          </Alert>
        )}

        {unassignedSystemsData ? (
          <>
            <Group justify="space-between" align="center">
              <Text size="sm" c="dimmed">
                {unassignedSystemsData.systems.length} unassigned system(s) found
                {selectedSystemIds.length > 0 && (
                  <Text component="span" inherit> • {selectedSystemIds.length} selected</Text>
                )}
              </Text>
            </Group>

            <SystemEnvironmentAndApplicationsTable
              systems={unassignedSystemsData.systems}
              selectedSystemIds={selectedSystemIds}
              onSelectionChange={setSelectedSystemIds}
              isLoading={isLoading}
            />

            {/* Footer with pagination and action button */}
            <Group justify="space-between" align="center">
              {totalPages > 1 ? (
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={handlePageChange}
                  size="sm"
                  withEdges
                  aria-label="unassigned-systems-pagination"
                />
              ) : (
                <Box/>
              )}
              <Button
                variant="light"
                size="sm"
                leftSection={<IconBuilding size={16}/>}
                color="blue"
                disabled={selectedSystemIds.length === 0}
                onClick={() => setAssignmentModalOpened(true)}
                aria-label="assign-systems-to-customer"
              >
                Assign to Customer
              </Button>
            </Group>
          </>
        ) : (
          !isLoading && !error && (
            <Alert
              variant="light"
              color="blue"
              icon={<IconInfoCircle/>}
              title="No unassigned systems"
            >
              There are currently no unassigned systems. All systems have been assigned to customers.
            </Alert>
          )
        )}
      </Stack>

      <SystemCustomerAssignmentModal
        opened={assignmentModalOpened}
        onClose={handleModalClose}
        systems={selectedSystems}
        title="Assign Systems to Customer"
      />
    </ProtectedComponent>
  );
}